import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";

import {
  CommercialDashboardSettings,
  useGetDashboardSettingsQuery,
  useUpdateDashboardSettingsMutation,
} from "../../store/apis/commercialDashboardApi";
import { setToastMessage } from "../../store/slices/app/appSlice";

interface Props {
  open: boolean;
  onClose: () => void;
}

type Form = {
  houseGroup: string;
  beapolo: string;
  amberDays: string;
  redDays: string;
  noResponseDays: string;
};

const asNumber = (value: string) =>
  value.trim() === ""
    ? null
    : Number(value.replace(/\./g, "").replace(",", "."));

/**
 * Los parámetros que fija Dirección: el objetivo anual de cada empresa (para este año) y
 * los umbrales de los avisos. Solo ADMIN guarda; el backend lo vuelve a comprobar.
 */
const SettingsDialog: React.FC<Props> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const year = new Date().getFullYear();
  const { data } = useGetDashboardSettingsQuery(undefined, { skip: !open });
  const [save, { isLoading: saving }] = useUpdateDashboardSettingsMutation();
  const settings = data?.data;
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);

  // El formulario se rellena una vez al abrir, con lo guardado, y se vacía al cerrar.
  // Sin el `form` en la guarda, cada nueva referencia de `settings` pisaría lo escrito.
  useEffect(() => {
    if (!open) {
      setForm(null);
      return;
    }
    if (!settings || form) return;
    const target = (businessUnit: string) =>
      settings.targets.find(
        (item) => item.year === year && item.businessUnit === businessUnit
      )?.amount;
    setForm({
      houseGroup: target("HOUSE_GROUP")?.toString() ?? "",
      beapolo: target("BEAPOLO")?.toString() ?? "",
      amberDays: String(settings.amberDays),
      redDays: String(settings.redDays),
      noResponseDays: String(settings.noResponseDays),
    });
    setError(null);
  }, [open, settings, form, year]);

  const handleSave = async () => {
    if (!form || !settings) return;
    const houseGroup = asNumber(form.houseGroup);
    const beapolo = asNumber(form.beapolo);
    if (
      [houseGroup, beapolo].some((value) => value !== null && !(value >= 0))
    ) {
      setError("Los objetivos deben ser importes de 0 o más.");
      return;
    }
    // Los objetivos de otros años se conservan tal cual.
    const targets: CommercialDashboardSettings["targets"] = [
      ...settings.targets.filter((item) => item.year !== year),
      ...(houseGroup !== null
        ? [{ year, businessUnit: "HOUSE_GROUP" as const, amount: houseGroup }]
        : []),
      ...(beapolo !== null
        ? [{ year, businessUnit: "BEAPOLO" as const, amount: beapolo }]
        : []),
    ];
    try {
      await save({
        targets,
        amberDays: Number(form.amberDays),
        redDays: Number(form.redDays),
        noResponseDays: Number(form.noResponseDays),
      }).unwrap();
      dispatch(
        setToastMessage({
          message: "Parámetros guardados",
          severity: "success",
        })
      );
      onClose();
    } catch (caught: unknown) {
      const message = (caught as { data?: { message?: string } })?.data
        ?.message;
      setError(message ?? "No se pudieron guardar los parámetros.");
    }
  };

  const field = (key: keyof Form, text: string, suffix: string) => (
    <TextField
      size="small"
      label={text}
      value={form?.[key] ?? ""}
      onChange={(event) =>
        setForm((current) =>
          current ? { ...current, [key]: event.target.value } : current
        )
      }
      inputProps={{ inputMode: "numeric" }}
      InputProps={{
        endAdornment: <InputAdornment position="end">{suffix}</InputAdornment>,
      }}
      fullWidth
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Parámetros del dashboard</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <Typography variant="subtitle2">Objetivo anual {year}</Typography>
          {field("houseGroup", "House Group", "€")}
          {field("beapolo", "BeApolo", "€")}
          <Typography variant="caption" color="text.secondary">
            Negocio ganado. Vacío = sin objetivo: no se pinta la barra.
          </Typography>
          <Typography variant="subtitle2">Avisos</Typography>
          {field("amberDays", "Ámbar a partir de", "días en fase")}
          {field("redDays", "Rojo a partir de", "días en fase")}
          {field("noResponseDays", "Entregada sin respuesta", "días")}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!form || saving}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog;
